'use client';

import { useTranslations } from 'next-intl';
import {
  Box,
  Button,
  Center,
  Container,
  HStack,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { IGallery } from '@/_types';
import { Link, useRouter } from '@/i18n/navigation';
import Header from '@/app/[locale]/(home)/_components/Header'
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from '@/components/ui/pagination';
import { useSearchParams } from 'next/navigation';
import GalleryCard from '../Card';
import Breadcrumbs from '@/_components/Breadcrumbs';
import { useMemo } from 'react';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { getPublicSeoOrigin } from '@/i18n/urls'

const Desktop = ({
  data,
  count,
}: {
  data: IGallery[];
  count: number | string;
}) => {
  const t = useTranslations();
  const searchParams = useSearchParams();
  const router = useRouter();
  const locale = useCustomLocale();
  const page = searchParams.get('page');

  const breadCrumbs = useMemo(
    () => [{ title: t('home'), link: '/' }, { title: t('gallery') }],
    [t],
  );

  const domain = getPublicSeoOrigin();

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.title,
      item: item.link ? `${domain}${item.link}` : undefined,
    })),
  };

  // Article JSON-LD for each gallery (optional)
  const galleryArticleJsonLd = data?.map((item) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: item.title?.[locale] || '',
    datePublished: item.created_at,
    dateModified: item.updated_at,
    image: item.images?.[0] || '',
    mainEntityOfPage: `${domain}/gallery/${item.id}`,
  }));

  if (!data?.length) {
    return (
      <Box bg='background'>
        <Header />
        <Container>
          <Center flexDir='column' minH='80vh'>
            <Text fontWeight={600} fontSize='xl' mt={4} mb={2}>
              {t('no_images_yet')}
            </Text>
            <Text mb={4} color='text.muted' textAlign='center' maxW='450px'>
              {t('gallery_will_be_updated_soon')}
            </Text>
            <Link href={'/'}>
              <Button
                bg='primary'
                color='primary.text'
                _hover={{
                  bg: 'primary.hover',
                }}
                size={'xl'}
                rounded={16}
                height={'56px'}
              >
                {t('back_to_home')}
              </Button>
            </Link>
          </Center>
        </Container>

        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />
      </Box>
    );
  }

  return (
    <Box bg='background'>
      <Header />
      <Container maxW={'1200px'} h={'fit'} mb={'127px'} p={0}>
        <Breadcrumbs data={breadCrumbs} />
        <Text fontWeight={700} fontSize={24} as={'h1'}>
          {t('gallery')}
        </Text>
        <Box mt={4} h={'fit'}>
          <SimpleGrid columns={3} gap={4}>
            {data?.map((el) => (
              <GalleryCard key={el.id} data={el} />
            ))}
          </SimpleGrid>
        </Box>
        {Number(count) > 10 && (
          <Center my={4}>
            <PaginationRoot
              count={Number(count)}
              pageSize={10}
              defaultPage={1}
              page={Number(page) || 1}
              onPageChange={(e) => router.push(`?page=${e.page}`)}
              variant='subtle'
            >
              <HStack>
                <PaginationPrevTrigger />
                <PaginationItems />
                <PaginationNextTrigger />
              </HStack>
            </PaginationRoot>
          </Center>
        )}
      </Container>

      {/* JSON-LD scripts */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      {galleryArticleJsonLd?.map((jsonLd, idx) => (
        <script
          key={idx}
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      ))}
    </Box>
  );
};

export default Desktop;
